import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useSystem, { useSystemForm } from '@hooks/useSystem'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'SlackWebhookSettingsModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateSystem: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const SlackWebhookSettingsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateSystem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { system } = modalPayload

  const {
    callbacks: { updateSystem: updateFn },
    updating,
  } = useSystem(system)

  const {
    entityState,
    entityState: {
      options,
    },
    handlers,
  } = useSystemForm(system, { validateOn: 'change' })

  const {
    slack_api_debug_webhook_url,
    slack_preflight_webhook_url,
    slack_publications_webhook_url,
    slack_templates_webhook_url,
    teams_api_debug_webhook_url,
    teams_preflight_webhook_url,
    teams_publications_webhook_url,
    teams_templates_webhook_url,
  } = options

  const updateSystemPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'System Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Slack / Teams Webhooks" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="Api Debug Url">
            <input name="options.slack_api_debug_webhook_url" value={slack_api_debug_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Slack Preflight Url" marginTop="large">
            <input name="options.slack_preflight_webhook_url" value={slack_preflight_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Slack Publications Url" marginTop="large">
            <input name="options.slack_publications_webhook_url" value={slack_publications_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Slack Templates Url" marginTop="large">
            <input name="options.slack_templates_webhook_url" value={slack_templates_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Slack Teams Api Debug Url" marginTop="large">
            <input name="options.teams_api_debug_webhook_url" value={teams_api_debug_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Teams Preflight Url" marginTop="large">
            <input name="options.teams_preflight_webhook_url" value={teams_preflight_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Teams Publications Url" marginTop="large">
            <input name="options.teams_publications_webhook_url" value={teams_publications_webhook_url} {...handlers} />
          </Form.Field>

          <Form.Field label="Teams Templates Url" marginTop="large">
            <input name="options.teams_templates_webhook_url" value={teams_templates_webhook_url} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateSystem(updateSystemPayload)}
          size="large"
        >
          Update System
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <SlackWebhookSettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
